import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguageInfo } from '../i18n/i18n';
import { usePreferences } from '../hooks/usePreferences';
import './LanguageSelector.css';

const LanguageSelector = ({ variant = 'dropdown', showFlag = true, showText = true, autoSave = true }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguageInfo());
  
  // Hook pour sauvegarder automatiquement la langue
  const { saveLanguage } = usePreferences();

  // Synchroniser l'affichage avec les changements de langue
  useEffect(() => {
    const updateCurrentLang = () => {
      const newLang = getCurrentLanguageInfo();
      if (newLang.code !== currentLang.code) {
        console.log(`🔄 LanguageSelector sync: ${currentLang.code} → ${newLang.code}`);
        setCurrentLang(newLang);
      }
    };

    // Listen to language changes
    window.addEventListener('languagechange', updateCurrentLang);
    
    // Check synchronization less frequently (every 5 seconds instead of 1)
    const interval = setInterval(updateCurrentLang, 5000);
    
    return () => {
      window.removeEventListener('languagechange', updateCurrentLang);
      clearInterval(interval);
    };
  }, [currentLang.code]); // Dependency pour éviter les loops

  const handleLanguageChange = async (langCode) => {
    // Change language immediately in the interface
    changeLanguage(langCode);
    setCurrentLang(getCurrentLanguageInfo());
    setIsOpen(false);
    
    // Trigger custom event to notify other components
    window.dispatchEvent(new Event('languagechange'));
    
    // Save to database if autoSave is enabled
    if (autoSave) {
      try {
        await saveLanguage(langCode);
        console.log(`🌍 Langue sauvegardée: ${langCode}`);
      } catch (error) {
        console.error('Erreur sauvegarde langue:', error);
      }
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Version compacte pour header
  if (variant === 'compact') {
    return (
      <div className="language-selector language-selector--compact">
        <button
          className="language-selector__trigger"
          onClick={toggleDropdown}
          title={t('settings.selectLanguage')}
        >
          {showFlag && <span className="language-selector__flag">{currentLang.flag}</span>}
          {showText && <span className="language-selector__text">{currentLang.code.toUpperCase()}</span>}
          <span className="language-selector__arrow">▼</span>
        </button>
        
        {isOpen && (
          <div className="language-selector__dropdown">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                className={`language-selector__option ${
                  currentLang.code === lang.code ? 'language-selector__option--active' : ''
                }`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="language-selector__flag">{lang.flag}</span>
                <span className="language-selector__name">{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full version for settings
  return (
    <div className="language-selector">
      <label className="language-selector__label">
        {t('settings.language')}
      </label>
      
      <div className="language-selector__wrapper">
        <button
          className="language-selector__trigger language-selector__trigger--full"
          onClick={toggleDropdown}
        >
          <div className="language-selector__current">
            {showFlag && <span className="language-selector__flag">{currentLang.flag}</span>}
            <span className="language-selector__name">{currentLang.name}</span>
          </div>
          <span className="language-selector__arrow">▼</span>
        </button>
        
        {isOpen && (
          <div className="language-selector__dropdown language-selector__dropdown--full">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                className={`language-selector__option ${
                  currentLang.code === lang.code ? 'language-selector__option--active' : ''
                }`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="language-selector__flag">{lang.flag}</span>
                <span className="language-selector__name">{lang.name}</span>
                {currentLang.code === lang.code && (
                  <span className="language-selector__check">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="language-selector__help">
        {t('settings.selectLanguage')}
      </div>
    </div>
  );
};

export default LanguageSelector;
